// src/pages/TermsOfServicePage.tsx - Valuate Terms of Service
import React from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  useTheme,
  alpha,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';

const TermsOfServicePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const sectionStyle = {
    mb: 4,
  };

  const headingStyle = {
    fontWeight: 600,
    mb: 2,
    color: theme.palette.text.primary,
  };

  const textStyle = {
    color: theme.palette.text.secondary,
    lineHeight: 1.8,
    mb: 2,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #0F172A 0%, #1E293B 100%)'
          : 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 4 }}
        >
          Back to Home
        </Button>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 4 }}>
            Terms of Service
          </Typography>

          <Typography sx={{ ...textStyle, mb: 4 }}>
            By accessing or using Valuate ("the Platform"), you agree to be bound by these
            Terms of Service. If you do not agree to these terms, do not use the Platform.
          </Typography>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              1. Description of Service
            </Typography>
            <Typography sx={textStyle}>
              Valuate provides AI-powered hotel valuation analysis tools. The Platform processes
              user-uploaded documents and data to generate property valuations, market analysis,
              and related reports using automated systems.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              2. No Financial or Investment Advice
            </Typography>
            <Typography sx={textStyle}>
              THE PLATFORM DOES NOT PROVIDE FINANCIAL, INVESTMENT, LEGAL, OR TAX ADVICE.
              All valuations, analyses, and reports generated by the Platform are for
              informational purposes only. You should not rely solely on Platform outputs
              when making investment decisions. Always conduct your own due diligence and
              consult with qualified professionals including licensed appraisers, financial
              advisors, and legal counsel before making any investment decisions.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              3. Accuracy Disclaimer
            </Typography>
            <Typography sx={textStyle}>
              While we strive to provide accurate analyses, valuations generated by the
              Platform are estimates based on the data provided and available market information.
              We do not guarantee the accuracy, completeness, or reliability of any valuation
              or analysis. Actual property values may differ significantly from Platform estimates.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              4. User Responsibilities
            </Typography>
            <Typography sx={textStyle}>
              You are responsible for: the accuracy of data and documents you upload;
              maintaining the confidentiality of your account credentials; ensuring you have
              the right to upload and process any documents submitted; and complying with
              all applicable laws when using the Platform.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              5. Prohibited Uses
            </Typography>
            <Typography sx={textStyle}>
              You may not: use the Platform for any unlawful purpose; upload malicious code
              or attempt to compromise Platform security; share your account credentials with
              others; resell or redistribute Platform outputs without authorization; or
              misrepresent Platform outputs as certified appraisals.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              6. Intellectual Property
            </Typography>
            <Typography sx={textStyle}>
              The Platform, including its software, design, and content, is owned by Valuate
              and protected by intellectual property laws. You retain ownership of data you
              upload but grant us a license to process it for providing our services.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              7. Limitation of Liability
            </Typography>
            <Typography sx={textStyle}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, VALUATE SHALL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
              OF PROFITS OR REVENUES, ARISING FROM YOUR USE OF THE PLATFORM. OUR TOTAL LIABILITY
              FOR ANY CLAIMS ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID
              US IN THE TWELVE MONTHS PRECEDING THE CLAIM.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              8. Indemnification
            </Typography>
            <Typography sx={textStyle}>
              You agree to indemnify and hold harmless Valuate and its officers, directors,
              employees, and agents from any claims, damages, or expenses arising from your
              use of the Platform or violation of these Terms.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              9. Termination
            </Typography>
            <Typography sx={textStyle}>
              We may suspend or terminate your access to the Platform at any time for violation
              of these Terms or for any other reason at our discretion. You may terminate your
              account at any time through your account settings.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              10. Changes to Terms
            </Typography>
            <Typography sx={textStyle}>
              We may modify these Terms at any time. Continued use of the Platform after
              changes constitutes acceptance of the modified Terms. We will notify users
              of significant changes.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              11. Governing Law
            </Typography>
            <Typography sx={textStyle}>
              These Terms shall be governed by and construed in accordance with applicable law,
              without regard to conflict of law principles.
            </Typography>
          </Box>

          <Box sx={sectionStyle}>
            <Typography variant="h5" sx={headingStyle}>
              12. Contact
            </Typography>
            <Typography sx={textStyle}>
              For questions about these Terms, please contact us through the contact form
              on our website.
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.disabled, mt: 4 }}
          >
            Last updated: January 2026
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;
